import {
  Check,
  ChevronDown,
  ChevronUp,
  CircleAlert,
  CircleCheck,
  Cog,
  ExternalLink,
  FileText,
  MessageCircleQuestion,
  Package,
} from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';

import { MarkdownRenderer } from '@/components/markdown';
import { useNotification } from '@/components/notifications';
import { cn } from '@/lib/utils';
import { undoChatApply, useChatApplyStore } from '@/stores/chat-apply-store';

import { getStructDisplayLabel } from '@/stores/helpers/node-naming';
import { useNodeStore } from '@/stores/node-store';

import { ClarificationCountdown } from './clarification-countdown';
import { describeOutcome, type RelayedRunEvent } from './run-event';
import { ChatArtifactCards } from '@/components/chat-artifact-cards';
import { ChatWorkflowCard } from './chat-workflow-card';
import { stripCustomInstructions } from './custom-instructions-popover';
import { FileChips, splitMessageAttachments } from './file-chips';
import { stripSkillDirective } from './helpers/skill-directive';
import { ModelFallbackNotice } from './model-fallback-notice';
import { REPLY_TEXT_CLASS, TurnBlocks } from './turn-blocks';
import { activityEvents, hasProse } from './turn-timeline';
import { TurnProgress } from './turn-progress';
import { useAgentChatContext } from './agent-chat-provider';
import type { AgentChatUiMessage } from './use-agent-chat';
import { useChatArtifactCards } from './use-chat-artifact-cards';
import { WorkflowMutationMessage } from '@/components/chat/workflow-mutation-message';
import { WorkflowRunCard } from './workflow-run-card';

interface MessageBubbleProps {
  message: AgentChatUiMessage;
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export function MessageBubble({ message }: MessageBubbleProps) {
  const runEvent = message._runEvent;
  if (runEvent?.kind === 'node_outcome') {
    return (
      <RunOutcomeLine outcome={runEvent.outcome} content={message.content} />
    );
  }
  return <TurnBubble message={message} />;
}

/**
 * How a relayed question ended: a muted line, not a turn. The transcript
 * never shows a question with no ending, so this is what closes it.
 */
function RunOutcomeLine({
  outcome,
  content,
}: {
  outcome: string | undefined;
  content: string;
}) {
  // The backend's sentence often opens with the same words as the label
  // ("No answer in time. …"); the label then says nothing the sentence does not.
  const describedLabel = describeOutcome(outcome);
  const label =
    describedLabel && !content.startsWith(describedLabel)
      ? describedLabel
      : null;
  // The backend bolds the node's name the way the rest of the transcript
  // does; this line is not markdown, so the bold is drawn here.
  const body = content.split(/\*\*(.+?)\*\*/g).map((part, i) =>
    i % 2 === 1 ? (
      <span key={i} className='font-medium'>
        {part}
      </span>
    ) : (
      part
    ),
  );
  return (
    <div
      className='flex items-center gap-2 text-xs text-muted-foreground'
      data-testid='run-outcome'
    >
      <Check className='size-3.5 shrink-0' />
      <span className='[overflow-wrap:anywhere]'>
        {label ? (
          <>
            <span className='font-medium'>{label}</span>
            {' · '}
            {body}
          </>
        ) : (
          body
        )}
      </span>
    </div>
  );
}

function badgeFor(
  event: RelayedRunEvent,
  canvasNodeLabel: string | undefined,
): {
  Icon: typeof MessageCircleQuestion;
  label: string;
  detail?: string;
} | null {
  // The canvas numbers unnamed nodes ("Agent 1"); the backend only knows a
  // name the user typed, so the canvas label wins while it is on screen.
  const node = canvasNodeLabel ?? event.nodeTitle ?? event.nodeId ?? 'A node';
  switch (event.kind) {
    case 'node_clarification':
      return {
        Icon: MessageCircleQuestion,
        label: node,
        detail: event.workflowName,
      };
    case 'node_review':
      return {
        Icon: FileText,
        label: node,
        detail: [event.workflowName, 'draft for review']
          .filter(Boolean)
          .join(' · '),
      };
    case 'node_review_reply':
      return { Icon: FileText, label: node, detail: 'replied' };
    case 'run_completed':
      return {
        Icon: CircleCheck,
        label: event.workflowName ?? 'Run',
        detail: 'run finished',
      };
    case 'run_failed':
    case 'run_stopped':
      return {
        Icon: CircleAlert,
        label: event.workflowName ?? 'Run',
        detail:
          event.kind === 'run_stopped' || event.terminalStatus === 'stopped'
            ? 'run stopped'
            : 'run failed',
      };
    default:
      return null;
  }
}

/**
 * The badge above a row a run wrote through the operator: which node or run,
 * what kind of row it is, and for a question still waiting, how long it
 * waits. A document draft gets a link to open it.
 */
function RunEventBadge({
  message,
  pending,
}: {
  message: AgentChatUiMessage;
  pending: boolean;
}) {
  const event = message._runEvent;
  const { visibleWorkflowId, composerDrafting } = useAgentChatContext();
  const onScreen =
    !!event?.workflowId &&
    !!visibleWorkflowId &&
    event.workflowId === visibleWorkflowId;
  const canvasNodeLabel = useNodeStore((state) =>
    onScreen && event?.nodeId
      ? getStructDisplayLabel(
          state.nodes.find((n) => n.id === event.nodeId)?.data?.struct,
        )
      : undefined,
  );
  const badge = event ? badgeFor(event, canvasNodeLabel) : null;
  if (!event || !badge) return null;
  const { Icon } = badge;
  return (
    <div
      className='flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground'
      data-testid='run-event-badge'
    >
      <Icon className='size-3.5 shrink-0' />
      <span>
        <span className='font-medium text-foreground'>{badge.label}</span>
        {badge.detail ? ` · ${badge.detail}` : ''}
      </span>
      {pending && event.kind === 'node_clarification' && (
        <ClarificationCountdown
          deadlineEpochMs={event.deadlineEpochMs}
          paused={composerDrafting}
        />
      )}
      {pending && event.kind === 'node_review' && (
        <span>Waiting for your word</span>
      )}
      {event.kind === 'node_review' && event.reviewUrl && (
        <a
          href={event.reviewUrl}
          target='_blank'
          rel='noreferrer'
          className='inline-flex items-center gap-1 underline underline-offset-2 hover:text-foreground'
        >
          <ExternalLink className='size-3' />
          Open the draft
        </a>
      )}
    </div>
  );
}

function TurnBubble({ message }: MessageBubbleProps) {
  const {
    applyMutations,
    onApplyMutations,
    applyMutationsDisabled,
    applyMutationsDisabledReason,
    visibleWorkflowId,
    pendingClarification,
    pendingReview,
  } = useAgentChatContext();
  const isUser = message.role === 'user';
  const timeline = isUser ? undefined : message._timeline;
  const hasEvents = (timeline?.length ?? 0) > 0;
  const timelineEvents = useMemo(
    () => activityEvents(timeline ?? []),
    [timeline],
  );
  const artifacts = useMemo(
    () => (isUser ? [] : (message._artifacts ?? [])),
    [isUser, message._artifacts],
  );
  const artifactCards = useChatArtifactCards(artifacts);

  // A surface host's canvas can switch to a different workflow after this
  // turn proposed mutations for the one it had open -- disable rather than
  // let a stale card land its changes on the wrong graph. Unaffected when
  // either side is unknown: the safe default is to allow, matching how the
  // rest of this surface treats an unresolved workflow id. `null` is not
  // "unknown" though -- it means the proposal was captured with no canvas
  // open at all, so there is no workflow it could ever legitimately match;
  // stays disabled no matter what canvas is open when the user gets to it.
  const targetsDifferentWorkflow =
    message._pendingMutationsWorkflowId === null ||
    (!!message._pendingMutationsWorkflowId &&
      !!visibleWorkflowId &&
      message._pendingMutationsWorkflowId !== visibleWorkflowId);
  const mutationsApplyDisabled =
    applyMutationsDisabled || targetsDifferentWorkflow;
  const mutationsApplyDisabledReason = targetsDifferentWorkflow
    ? 'This proposal was made for a different workflow — open it to apply this change'
    : applyMutationsDisabledReason;

  const notification = useNotification();
  const applyStatus = useChatApplyStore(
    (state) => state.records[message._localId]?.status,
  );
  const canUndoMutations = useChatApplyStore(
    (state) => !!state.records[message._localId]?.snapshots,
  );
  const mutationsUndone = applyStatus === 'undone';
  const mutationsApplied = !!message._mutationsApplied && !mutationsUndone;
  const handleUndoMutations = useCallback(() => {
    const result = undoChatApply(message._localId);
    if (result === 'edited-since') {
      notification.info(
        'The workflow was edited after these changes were applied, so they can’t be undone from here without losing those edits. Use the canvas undo instead.',
      );
    } else if (result === 'unavailable') {
      notification.info('These changes can no longer be undone from chat.');
    }
  }, [message._localId, notification]);

  const [showInstructions, setShowInstructions] = useState(false);

  const { text, attachments, instructions, appliedSkill } = useMemo(() => {
    if (!isUser) {
      return {
        text: message.content,
        attachments: [],
        instructions: undefined,
        appliedSkill: undefined,
      };
    }
    // Strip order mirrors wrap order (custom instructions outermost,
    // then the skill directive) -- see use-agent-chat.sendMessage.
    const stripped = stripCustomInstructions(message.content);
    const withoutSkill = stripSkillDirective(stripped.text);
    return {
      ...splitMessageAttachments(withoutSkill.text),
      instructions: stripped.instructions,
      appliedSkill: withoutSkill.skill,
    };
  }, [isUser, message.content]);

  // A turn that describes a workflow usually links it in the prose too, and
  // that link unfurls into the very same card, so the structured one below
  // the text would just be a second copy of it. A plain substring search
  // would also match a prefix collision (`wf-1` inside `wf-123`) or a
  // mention outside any real link (prose, a code span) -- require the id to
  // sit in an actual markdown link target, at a real path boundary. Excludes
  // `?`/`#` from the chars before `/workflow/`, matching the unfurl side
  // (`workflowIdFromHref`), which only cards on the URL's pathname -- an id
  // that only shows up in a query string or fragment never actually unfurls.
  const textLinksWorkflow =
    !!message._workflowId &&
    new RegExp(
      `]\\([^)\\s?#]*\\/workflow\\/${escapeRegExp(message._workflowId)}(?:[/?#)]|$)`,
    ).test(text ?? '');
  // A row relayed from a run carries its workflow id for the record, not
  // to present the workflow: its badge already names the node and the run.
  const showWorkflowCard =
    !!message._workflowId && !textLinksWorkflow && !message._runEvent;

  // An answer the user picked reads as a choice, not as something they
  // happened to type. Free text they typed instead is just a message.
  const pickedOption = isUser
    ? message._decisionResponse?.option_label
    : undefined;

  // Between steps the live group header is the progress, and a stopped turn
  // already shows its prose inline -- an empty bubble would only add a stray
  // cursor or an "(empty message)" under them.
  const bubbleIdle =
    hasEvents &&
    !text &&
    (!!message._streaming || (hasProse(timeline) && !message._stalled));
  const showBubble =
    !bubbleIdle && (!isUser || !!text || attachments.length === 0);

  if (pickedOption) {
    return (
      <div className='flex min-w-0 flex-col items-end gap-3'>
        <div className='flex max-w-[480px] items-center gap-2 rounded-xl border border-border bg-accent/60 px-3 py-2 text-sm text-foreground'>
          <Check className='size-3.5 shrink-0 text-muted-foreground' />
          <span className='min-w-0 wrap-break-word'>{pickedOption}</span>
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        'flex min-w-0 flex-col gap-3',
        isUser ? 'items-end' : 'items-start',
      )}
    >
      {timeline && hasEvents && (
        <div className='w-full space-y-3'>
          <TurnBlocks
            timeline={timeline}
            streaming={!!message._streaming}
            replyInProgress={!!text}
          />
          <ModelFallbackNotice events={timelineEvents} />
        </div>
      )}
      {!isUser && message._runEvent && (
        <RunEventBadge
          message={message}
          pending={
            !!message._runEvent.requestKey &&
            (pendingClarification?.requestKey ===
              message._runEvent.requestKey ||
              pendingReview?.requestKey === message._runEvent.requestKey)
          }
        />
      )}
      {showBubble && (
        <div
          className={cn(
            'min-w-0 text-base text-foreground',
            isUser
              ? 'max-w-[480px] whitespace-pre-wrap rounded-xl bg-accent px-4 py-3'
              : 'w-full',
          )}
        >
          {message._streaming && !text && !hasEvents ? (
            <TurnProgress
              events={[]}
              startedAt={message._startedAt}
              thinking={message._thinking}
            />
          ) : message._stalled ? (
            <span className='italic text-muted-foreground'>
              {message.content}
            </span>
          ) : isUser ? (
            text
          ) : (
            <>
              {text ? (
                <MarkdownRenderer
                  content={text}
                  isStreaming={!!message._streaming}
                  className={REPLY_TEXT_CLASS}
                />
              ) : message._streaming ? (
                ''
              ) : message._runIds?.length ||
                message._workflowId ||
                artifacts.length > 0 ? (
                ''
              ) : (
                '(empty message)'
              )}
              {message._streaming && !message._pending && (
                <span className='inline-block w-1.5 h-3.5 ml-0.5 align-baseline bg-foreground/60 animate-pulse' />
              )}
            </>
          )}
        </div>
      )}
      {attachments.length > 0 && (
        <FileChips files={attachments} className={cn(isUser && 'items-end')} />
      )}
      {/* The workflow cards share one slot: a run card takes the summary
          card's place as soon as the workflow is launched, and a turn that
          launches several gets one card each, in the order they started.
          A run card is live progress and so earns its place mid-turn; the
          summary card is context for what the agent is still saying, and
          would otherwise appear under the open activity feed and move twice
          before the turn settles. */}
      {!isUser &&
        (message._runIds?.length
          ? message._runIds.map((runId) => (
              <WorkflowRunCard
                key={runId}
                runId={runId}
                dispatchWorkflowId={message._workflowId}
                isHistorical={!!message._runsAreHistorical}
              />
            ))
          : showWorkflowCard && (
              <ChatWorkflowCard
                workflowId={message._workflowId!}
                visible={!message._streaming}
              />
            ))}
      <ChatArtifactCards cards={artifactCards} />
      {!isUser &&
        !!onApplyMutations &&
        !message._streaming &&
        (message._pendingMutations?.length ?? 0) > 0 && (
          <WorkflowMutationMessage
            mutations={message._pendingMutations!}
            applied={mutationsApplied}
            undone={mutationsUndone}
            onUndo={
              applyStatus === 'applied' && canUndoMutations
                ? handleUndoMutations
                : undefined
            }
            onApply={() =>
              applyMutations(
                message._localId,
                message._pendingMutations!,
                message._pendingMutationsWorkflowId,
              )
            }
            applyDisabled={mutationsApplyDisabled}
            applyDisabledReason={mutationsApplyDisabledReason}
          />
        )}
      {appliedSkill && (
        <div className='flex items-center justify-end gap-1 text-[10px] text-muted-foreground'>
          <Package className='size-2.5' />
          {`Skill applied: ${appliedSkill.name}`}
        </div>
      )}
      {instructions && (
        <div className='flex flex-col items-end gap-0.5'>
          <button
            type='button'
            onClick={() => setShowInstructions((v) => !v)}
            className='flex items-center gap-1 text-[10px] text-muted-foreground hover:text-foreground transition-colors'
          >
            <Cog className='size-2.5' />
            Custom instructions applied
            {showInstructions ? (
              <ChevronUp className='size-2.5' />
            ) : (
              <ChevronDown className='size-2.5' />
            )}
          </button>
          {showInstructions && (
            <div className='rounded border border-border/60 bg-muted/40 px-2 py-1 text-[10px] text-muted-foreground whitespace-pre-wrap text-left'>
              {instructions}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
